/* eslint-disable max-len */
import React from 'react'
import Button from '@components/common/Button/Button.component'
import { AudioPlayer } from '@components/ui'
import AIResponseDisplay from '../AIResponseDisplay'
import { QUICK_NOTE_AI_MODE_CONFIG } from './noteEditor.constants'
import type { NoteRecordingTake, QuickNoteAiMode } from './noteEditor.types'

type RecordingOption = {
  label: string
  value: string
}

type RecordingSelectProps = {
  menuRef: React.RefObject<HTMLDivElement>
  open: boolean
  label: string
  wide?: boolean
  options: RecordingOption[]
  selectedValue: string
  onToggle: () => void
  onSelect: (value: string) => void
}

const DropdownCheckIcon = (): JSX.Element => (
  <span className="note-recording-dropdown-item-icon" aria-hidden="true">
    <svg
      width="14"
      height="10"
      viewBox="0 0 14 10"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M13.0333 0.29312C13.2208 0.480647 13.3261 0.734956 13.3261 1.00012C13.3261 1.26528 13.2208 1.51959 13.0333 1.70712L5.53796 9.20245C5.4389 9.30153 5.3213 9.38012 5.19187 9.43374C5.06244 9.48736 4.92372 9.51496 4.78362 9.51496C4.64352 9.51496 4.5048 9.48736 4.37537 9.43374C4.24594 9.38012 4.12834 9.30153 4.02929 9.20245L0.305288 5.47912C0.209778 5.38687 0.133596 5.27653 0.0811869 5.15452C0.0287779 5.03252 0.00119157 4.9013 3.77566e-05 4.76852C-0.00111606 4.63574 0.0241857 4.50406 0.0744666 4.38117C0.124748 4.25827 0.199001 4.14662 0.292893 4.05272C0.386786 3.95883 0.498438 3.88458 0.621334 3.8343C0.744231 3.78402 0.87591 3.75872 1.00869 3.75987C1.14147 3.76102 1.27269 3.78861 1.39469 3.84102C1.5167 3.89343 1.62704 3.96961 1.71929 4.06512L4.78329 7.12912L11.6186 0.29312C11.7115 0.200193 11.8218 0.126477 11.9431 0.0761817C12.0645 0.0258869 12.1946 0 12.326 0C12.4573 0 12.5874 0.0258869 12.7088 0.0761817C12.8302 0.126477 12.9404 0.200193 13.0333 0.29312Z"
        fill="currentColor"
      />
    </svg>
  </span>
)

const RecordingSelect = ({
  menuRef,
  open,
  label,
  wide = false,
  options,
  selectedValue,
  onToggle,
  onSelect,
}: RecordingSelectProps): JSX.Element => (
  <div className="note-recording-dropdown" ref={menuRef}>
    <button
      type="button"
      className={`template-dropdown-button note-recording-dropdown-button ${
        open ? 'template-dropdown-open' : ''
      }`}
      onClick={onToggle}
      aria-haspopup="listbox"
      aria-expanded={open}
    >
      <span>{label}</span>
      <i
        className={`mgc ${open ? 'mgc_up_small_fill' : 'mgc_down_small_fill'}`}
      />
    </button>
    {open && (
      <div
        className={`note-recording-dropdown-menu${
          wide ? ' note-recording-dropdown-menu-wide' : ''
        }`}
        role="listbox"
      >
        {options.map((option) => {
          const isActive = selectedValue === option.value
          return (
            <button
              key={option.value}
              type="button"
              className={`note-recording-dropdown-item ${isActive ? 'active' : ''}`}
              onClick={() => onSelect(option.value)}
              role="option"
              aria-selected={isActive}
            >
              <span className="note-recording-dropdown-item-label">
                {option.label}
              </span>
              {isActive && <DropdownCheckIcon />}
            </button>
          )
        })}
      </div>
    )}
  </div>
)

type NoteEditorQuickNoteProps = {
  shouldShowAiRecordingBar: boolean
  aiNoteStructureDropdownRef: React.RefObject<HTMLDivElement>
  showAiNoteStructureDropdown: boolean
  setShowAiNoteStructureDropdown: React.Dispatch<React.SetStateAction<boolean>>
  setShowAiToneDropdown: React.Dispatch<React.SetStateAction<boolean>>
  aiNoteStructureLabel: string
  aiNoteStructureOptions: RecordingOption[]
  aiNoteStructure: string
  onAiNoteStructureSelect: (value: string) => void
  aiToneDropdownRef: React.RefObject<HTMLDivElement>
  showAiToneDropdown: boolean
  aiToneLabel: string
  aiToneOptions: RecordingOption[]
  aiTone: string
  onAiToneSelect: (value: string) => void
  shouldShowAiPlaybackControls: boolean
  showRecordingTakeNav: boolean
  activeTakeIndex: number
  recordingTakes: NoteRecordingTake[]
  onPreviousRecordingTake: () => void
  audioUrl: string | null
  onRecordingTakeEnded: () => void
  onNextRecordingTake: () => void
  showTranscription: boolean
  onHideTranscription?: () => void
  onViewTranscription?: () => void
  shouldShowAiPreview: boolean
  aiResponse: string
  showEnhanceDropdown: boolean
  isAiLoading: boolean
  aiError: string | null
  onRegenerate: () => void
  onEnhance: (enhanceType: string) => void
  setShowEnhanceDropdown: (show: boolean) => void
  quickNoteInputRef: React.RefObject<HTMLInputElement>
  quickNote: string
  setQuickNote: (value: string) => void
  quickNoteAiMode: QuickNoteAiMode
  setQuickNoteAiMode: (mode: QuickNoteAiMode) => void
  showAiOverlay: boolean
  setIsTyping: (value: boolean) => void
  isTyping: boolean
  onQuickNoteKeyPress: (event: React.KeyboardEvent) => void
  hasAiResponse: boolean
  onQuickNoteSend: () => void
  hasQuickNoteText: boolean
  canUseNotesTextAi: boolean
  notesTextAiDisabledTitle: string | undefined
  onDismissAiResponse: () => void
  onReplaceAiResponse: () => void
  onInsertAiResponse: () => void
  canCommitAiResponse: boolean
}

const NoteEditorQuickNote = ({
  shouldShowAiRecordingBar,
  aiNoteStructureDropdownRef,
  showAiNoteStructureDropdown,
  setShowAiNoteStructureDropdown,
  setShowAiToneDropdown,
  aiNoteStructureLabel,
  aiNoteStructureOptions,
  aiNoteStructure,
  onAiNoteStructureSelect,
  aiToneDropdownRef,
  showAiToneDropdown,
  aiToneLabel,
  aiToneOptions,
  aiTone,
  onAiToneSelect,
  shouldShowAiPlaybackControls,
  showRecordingTakeNav,
  activeTakeIndex,
  recordingTakes,
  onPreviousRecordingTake,
  audioUrl,
  onRecordingTakeEnded,
  onNextRecordingTake,
  showTranscription,
  onHideTranscription,
  onViewTranscription,
  shouldShowAiPreview,
  aiResponse,
  showEnhanceDropdown,
  isAiLoading,
  aiError,
  onRegenerate,
  onEnhance,
  setShowEnhanceDropdown,
  quickNoteInputRef,
  quickNote,
  setQuickNote,
  quickNoteAiMode,
  setQuickNoteAiMode,
  showAiOverlay,
  setIsTyping,
  isTyping,
  onQuickNoteKeyPress,
  hasAiResponse,
  onQuickNoteSend,
  hasQuickNoteText,
  canUseNotesTextAi,
  notesTextAiDisabledTitle,
  onDismissAiResponse,
  onReplaceAiResponse,
  onInsertAiResponse,
  canCommitAiResponse,
}: NoteEditorQuickNoteProps): JSX.Element => (
  <div className="quick-note-input-container">
    {shouldShowAiRecordingBar && (
      <div
        className={`note-recording-bar ${
          shouldShowAiRecordingBar ? 'note-recording-bar-ai' : ''
        }`}
      >
        <div className="note-recording-bar-left">
          <span className="note-recording-bar-icon">
            <i className="mgc mgc_mic_ai_fill" />
          </span>
          <RecordingSelect
            menuRef={aiNoteStructureDropdownRef}
            open={showAiNoteStructureDropdown}
            label={aiNoteStructureLabel}
            wide
            options={aiNoteStructureOptions}
            selectedValue={aiNoteStructure}
            onToggle={() => {
              setShowAiNoteStructureDropdown((prevState) => !prevState)
              setShowAiToneDropdown(false)
            }}
            onSelect={onAiNoteStructureSelect}
          />
          <RecordingSelect
            menuRef={aiToneDropdownRef}
            open={showAiToneDropdown}
            label={aiToneLabel}
            options={aiToneOptions}
            selectedValue={aiTone}
            onToggle={() => {
              setShowAiToneDropdown((prevState) => !prevState)
              setShowAiNoteStructureDropdown(false)
            }}
            onSelect={onAiToneSelect}
          />
        </div>
        {shouldShowAiPlaybackControls && (
          <>
            <div className="note-recording-bar-center">
              {showRecordingTakeNav ? (
                <span className="note-recording-take-label" aria-live="polite">
                  Recording {activeTakeIndex + 1} of {recordingTakes.length}
                </span>
              ) : null}
              <div className="note-recording-take-player-row">
                {showRecordingTakeNav ? (
                  <button
                    type="button"
                    className="note-recording-take-nav-btn"
                    aria-label="Previous recording"
                    disabled={activeTakeIndex <= 0}
                    onClick={onPreviousRecordingTake}
                  >
                    <i
                      className="mgc mgc_skip_previous_line"
                      aria-hidden="true"
                    />
                  </button>
                ) : null}
                <AudioPlayer
                  key={audioUrl ?? 'no-audio'}
                  src={audioUrl ?? undefined}
                  className="note-recording-audio-player"
                  onEnded={onRecordingTakeEnded}
                />
                {showRecordingTakeNav ? (
                  <button
                    type="button"
                    className="note-recording-take-nav-btn"
                    aria-label="Next recording"
                    disabled={activeTakeIndex >= recordingTakes.length - 1}
                    onClick={onNextRecordingTake}
                  >
                    <i
                      className="mgc mgc_skip_forward_line"
                      aria-hidden="true"
                    />
                  </button>
                ) : null}
              </div>
            </div>
            <div className="note-recording-bar-right">
              <button
                type="button"
                className="button button-sm button-secondary"
                onClick={() => {
                  if (showTranscription) onHideTranscription?.()
                  else onViewTranscription?.()
                }}
              >
                {showTranscription
                  ? 'Hide Transcription'
                  : 'View Transcription'}
              </button>
            </div>
          </>
        )}
      </div>
    )}
    {shouldShowAiPreview && (
      <div
        className="ai-response-container notes-ai-preview-panel"
        aria-live="polite"
      >
        <AIResponseDisplay
          aiResponse={aiResponse}
          showAiActions={shouldShowAiPreview}
          showEnhanceDropdown={showEnhanceDropdown}
          renderPreview={true}
          isLoading={isAiLoading}
          error={aiError}
          onRegenerate={onRegenerate}
          onEnhance={onEnhance}
          onToggleEnhanceDropdown={() =>
            setShowEnhanceDropdown(!showEnhanceDropdown)
          }
          onCloseEnhanceDropdown={() => setShowEnhanceDropdown(false)}
        />
      </div>
    )}
    <div className="quick-note-input-inner">
      <div className="quick-note-input-left">
        <div className="quick-note-input-wrapper">
          <input
            ref={quickNoteInputRef}
            type="text"
            value={quickNote}
            placeholder={QUICK_NOTE_AI_MODE_CONFIG[quickNoteAiMode].placeholder}
            onChange={(e) => {
              setQuickNote(e.target.value)
              if (showAiOverlay && e.target.value.length > 0) setIsTyping(true)
            }}
            onFocus={(e) => {
              if (showAiOverlay) setIsTyping(true)
              if (quickNote.trim()) {
                setTimeout(() => {
                  const input = e.target as HTMLInputElement
                  const length = input.value.length
                  input.setSelectionRange(length, length)
                }, 0)
              }
            }}
            onBlur={() => {
              setIsTyping(false)
            }}
            onKeyPress={onQuickNoteKeyPress}
            className={`quick-note-input ${showAiOverlay ? 'ai-mode' : ''} ${isTyping ? 'typing' : ''} ${quickNote.trim() ? 'has-content' : ''}`}
            style={{
              paddingLeft: showAiOverlay ? '80px' : '14px',
            }}
          />
          {showAiOverlay && (
            <div
              className="quick-note-ai-overlay"
              onClick={() => {
                const input = document.querySelector(
                  '.quick-note-input',
                ) as HTMLInputElement
                if (input) input.focus()
              }}
            >
              <div className="ai-overlay-container">
                <div className="quick-note-ai-mode quick-note-ai-mode--inline">
                  <div className="quick-note-ai-mode-buttons">
                    <button
                      type="button"
                      className={`quick-note-ai-mode-button ${
                        quickNoteAiMode === 'paraphrase'
                          ? 'quick-note-ai-mode-button-active'
                          : ''
                      }`}
                      onClick={(event) => {
                        event.stopPropagation()
                        setQuickNoteAiMode('paraphrase')
                        quickNoteInputRef.current?.focus()
                      }}
                      aria-pressed={quickNoteAiMode === 'paraphrase'}
                      title="Type here to paraphrase with AI"
                    >
                      <i className="mgc mgc_pencil_2_ai_fill" />
                    </button>
                    <button
                      type="button"
                      className={`quick-note-ai-mode-button ${
                        quickNoteAiMode === 'ask'
                          ? 'quick-note-ai-mode-button-active'
                          : ''
                      }`}
                      onClick={(event) => {
                        event.stopPropagation()
                        setQuickNoteAiMode('ask')
                        quickNoteInputRef.current?.focus()
                      }}
                      aria-pressed={quickNoteAiMode === 'ask'}
                      title="Ask AI anything"
                    >
                      <i className="mgc mgc_magic_3_fill" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="quick-note-input-right">
        {!hasAiResponse && (
          <button
            type="button"
            className="quick-note-send-btn"
            onClick={onQuickNoteSend}
            disabled={!hasQuickNoteText || !canUseNotesTextAi || isAiLoading}
            aria-label={notesTextAiDisabledTitle || 'Send quick note to AI'}
            title={notesTextAiDisabledTitle || 'Send quick note to AI'}
          >
            <svg
              width="17"
              height="16"
              viewBox="0 0 17 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M15.5997 9.10228C16.7074 8.58282 16.7074 7.00604 15.599 6.48726L2.05957 0.14C0.947798 -0.380825 -0.264738 0.634274 0.0511612 1.82026L1.38761 6.83176L7.05609 6.83176C7.30851 6.83614 7.54911 6.9395 7.72607 7.11956C7.90303 7.29961 8.00218 7.54197 8.00218 7.79443C8.00218 8.04689 7.90302 8.28925 7.72607 8.46931C7.54911 8.64937 7.30851 8.75272 7.05609 8.75711L1.38761 8.75711L0.0518421 13.7693C-0.264737 14.9546 0.948479 15.969 2.05957 15.4489L15.5997 9.10228Z"
                fill="#185DD8"
              />
            </svg>
          </button>
        )}
        {(hasAiResponse || shouldShowAiPreview) && (
          <div className="ai-action-buttons">
            <Button
              size="sm"
              className="button-secondary"
              onClick={onDismissAiResponse}
              title="Dismiss AI response"
            >
              Dismiss
            </Button>
            {hasAiResponse && (
              <>
                <Button
                  size="sm"
                  className="button-secondary"
                  onClick={onReplaceAiResponse}
                  disabled={!canCommitAiResponse}
                  title={
                    canCommitAiResponse
                      ? 'Replace note with AI response'
                      : notesTextAiDisabledTitle || 'Waiting for AI response'
                  }
                >
                  Replace
                </Button>
                <Button
                  size="sm"
                  className="button-primary"
                  onClick={onInsertAiResponse}
                  disabled={!canCommitAiResponse}
                  title={
                    canCommitAiResponse
                      ? 'Insert AI response into note'
                      : notesTextAiDisabledTitle || 'Waiting for AI response'
                  }
                >
                  Insert
                </Button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  </div>
)

export default NoteEditorQuickNote
